import { FC, useEffect, useState } from 'react'
import { useGetSsccXmlValidationList } from 'framework/api/methods/ssccXml/useGetSsccXmlValidationList'

import { formatSsccXmlDate } from './ssccXmlFormat'
import { SsccXmlColumn, SsccXmlSort, SsccXmlTable } from './SsccXmlTable'

export interface SsccXmlValidationRow {
  id: string
  orderNumber: string
  orderDate?: string | null
}

export interface SsccXmlValidationTabProps {
  search: string
  onPendingCountChange: (count: number) => void
  /** Opens the Fix SSCC XML pop-up (BDL20-3995); the Fix button is disabled while this is not provided. */
  onFix?: (row: SsccXmlValidationRow) => void
  /** Bumped by the parent to reload after a successful retrigger. */
  refreshKey?: number
}

/** BDL20-4066: the SSCC XML Validation tab — held TWD orders with a Fix action. */
export const SsccXmlValidationTab: FC<SsccXmlValidationTabProps> = ({
  search,
  onPendingCountChange,
  onFix,
  refreshKey,
}: SsccXmlValidationTabProps) => {
  const [pageSize, setPageSize] = useState(10)
  const [pageNumber, setPageNumber] = useState(1)
  const [sort, setSort] = useState<SsccXmlSort>({
    sortBy: 'orderDate',
    sortDirection: 'desc',
  })

  useEffect(() => setPageNumber(1), [search, pageSize, sort])

  const { data, isLoading, refetch } = useGetSsccXmlValidationList({
    search,
    sortBy: sort.sortBy,
    sortDirection: sort.sortDirection,
    take: pageSize,
    skip: (pageNumber - 1) * pageSize,
  })

  useEffect(() => {
    if (refreshKey) refetch()
  }, [refreshKey])

  useEffect(() => {
    if (typeof data?.pendingCount === 'number')
      onPendingCountChange(data.pendingCount)
  }, [data?.pendingCount])

  const columns: SsccXmlColumn<SsccXmlValidationRow>[] = [
    {
      key: 'orderNumber',
      header: 'Order Number',
      width: '260px',
      sortable: true,
      render: (row) => (
        <span className='font-bold text-[#2A2A2D]'>{row.orderNumber}</span>
      ),
    },
    {
      key: 'orderDate',
      header: 'Order Date',
      width: 'minmax(0,1fr)',
      sortable: true,
      render: (row) => formatSsccXmlDate(row.orderDate),
    },
    {
      key: 'action',
      header: 'Action',
      width: '110px',
      align: 'right',
      render: (row) => (
        <button
          type='button'
          disabled={!onFix}
          title={onFix ? undefined : 'Coming soon'}
          onClick={() => onFix?.(row)}
          className='inline-flex items-center gap-1 border border-[#F0A04B] bg-white text-[#C47A2C] rounded-md pl-3 pr-2 py-[5px] text-[13px] font-extrabold hover:bg-[#FDF1E3] disabled:opacity-50 disabled:cursor-not-allowed'>
          Fix
          <i className='ri-arrow-right-s-line' />
        </button>
      ),
    },
  ]

  return (
    <SsccXmlTable
      columns={columns}
      rows={data?.items ?? []}
      rowKey={(row) => row.id}
      totalCount={data?.totalCount ?? 0}
      pageSize={pageSize}
      pageNumber={pageNumber}
      isLoading={isLoading}
      sort={sort}
      onSortChange={setSort}
      onPageChange={setPageNumber}
      onPageSizeChange={setPageSize}
    />
  )
}

export default SsccXmlValidationTab
